import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, BookOpen, Phone } from 'lucide-react';
import { publicApi, imageUrl } from '../api';
import { contactInfo } from '../mock';

export default function Speisekarte() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const touchX = useRef(null);
  const n = pages.length;

  useEffect(() => {
    publicApi.getAlbum('speisekarte').then(setPages).catch(() => setPages([])).finally(() => setLoading(false));
  }, []);

  const go = (d) => setIdx((v) => Math.min(Math.max(v + d, 0), n - 1));

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setIdx((v) => Math.min(v + 1, n - 1));
      if (e.key === 'ArrowLeft') setIdx((v) => Math.max(v - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [n]);

  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <div className="bg-[#faf6f0] pt-32 pb-24" data-testid="speisekarte-page">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="text-amber-600 tracking-[0.3em] text-xs uppercase">Unsere Gerichte</span>
          <h1 className="font-serif text-5xl md:text-6xl text-[#1a1410] mt-4">Speisekarte</h1>
          <div className="w-16 h-[3px] bg-amber-500 mx-auto mt-6"/>
          <p className="text-[#5a4a3d] max-w-2xl mx-auto mt-6">Blättern Sie durch unsere Speisekarte — österreichische Klassiker, frisch für Sie zubereitet.</p>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl border border-amber-100 p-12 text-center text-[#5a4a3d] animate-pulse">Lädt …</div>
        ) : n === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-amber-100 p-12 text-center" data-testid="speisekarte-empty">
            <div className="mx-auto w-20 h-20 rounded-full bg-amber-50 flex items-center justify-center mb-6"><BookOpen size={36} className="text-amber-500"/></div>
            <h3 className="font-serif text-2xl text-[#1a1410] mb-3">Speisekarte folgt in Kürze</h3>
            <p className="text-[#5a4a3d] max-w-md mx-auto">Die Speisekarte wird bald hier veröffentlicht. Schauen Sie bald wieder vorbei.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-amber-100">
            <div className="bg-[#1a1410] text-white px-6 py-4 flex items-center justify-between">
              <span className="font-serif text-lg flex items-center gap-3"><BookOpen size={20} className="text-amber-400"/> Speisekarte</span>
              <span className="text-sm text-amber-100/80" data-testid="speisekarte-page-indicator">Seite {idx + 1} / {n}</span>
            </div>
            <div className="relative bg-[#f3ebdd] p-4 md:p-6 flex items-center justify-center min-h-[50vh] select-none"
              onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }} onTouchEnd={onTouchEnd}>
              <img key={pages[idx].id} src={imageUrl(pages[idx].image_url)} alt={`Speisekarte Seite ${idx + 1}`} data-testid="speisekarte-image"
                className="max-w-full max-h-[80vh] rounded-lg shadow-lg animate-[fadeIn_.35s_ease]"/>
              <PageArrow side="left" disabled={idx === 0} onClick={() => go(-1)}/>
              <PageArrow side="right" disabled={idx === n - 1} onClick={() => go(1)}/>
            </div>
            {n > 1 && (
              <div className="flex gap-2 overflow-x-auto p-4 border-t border-amber-100 justify-center" data-testid="speisekarte-thumbs">
                {pages.map((p, i) => (
                  <button key={p.id} onClick={() => setIdx(i)} data-testid={`speisekarte-thumb-${i + 1}`}
                    className={`shrink-0 w-14 h-20 rounded-md overflow-hidden border-2 transition-colors ${i === idx ? 'border-amber-500' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                    <img src={imageUrl(p.image_url)} alt={`Seite ${i + 1}`} className="w-full h-full object-cover"/>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-10 text-center">
          <a href={`tel:${contactInfo.phone}`} className="inline-flex items-center gap-3 px-6 py-4 rounded-full bg-amber-500 hover:bg-amber-400 text-[#1a1410] font-bold transition-colors shadow-lg" data-testid="speisekarte-call-btn">
            <Phone size={20}/> Fragen? {contactInfo.phone}
          </a>
        </div>
      </div>
    </div>
  );
}

function PageArrow({ side, disabled, onClick }) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button onClick={onClick} disabled={disabled} aria-label={side === 'left' ? 'Vorherige Seite' : 'Nächste Seite'} data-testid={`speisekarte-${side === 'left' ? 'prev' : 'next'}`}
      className={`absolute top-1/2 -translate-y-1/2 ${side === 'left' ? 'left-2 md:left-4' : 'right-2 md:right-4'} w-12 h-12 rounded-full bg-[#1a1410]/80 hover:bg-amber-500 text-white flex items-center justify-center shadow-lg transition-colors disabled:opacity-20 disabled:pointer-events-none`}>
      <Icon size={26}/>
    </button>
  );
}
